import { Link2, X } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { PendingAttachment } from '../../lib/useCommentAttachments';
import { Spinner } from '@monksflow/monks-ui';

interface Props {
  attachments: PendingAttachment[];
  onRemove: (localId: string) => void;
  disabled?: boolean;
}

const chipBaseClasses =
  'inline-flex h-7 items-center gap-1.5 rounded-full bg-muted text-sm text-foreground transition-colors';
const FILENAME_TRUNCATE_AT = 20;

function truncateFilename(filename: string): string {
  if (filename.length <= FILENAME_TRUNCATE_AT) return filename;
  return `${filename.slice(0, FILENAME_TRUNCATE_AT)}…`;
}

export function PendingAttachmentChips({ attachments, onRemove, disabled }: Props) {
  const { t } = useTranslation('projects');

  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-row flex-wrap gap-2">
      {attachments.map((a) => {
        const isUploading = a.status === 'uploading';
        const truncated = truncateFilename(a.filename);

        const closeButton = (
          <button
            type="button"
            aria-label={t('projectComment.pendingAttachmentChips.removeAttachment', {
              filename: a.filename,
            })}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onRemove(a.localId);
            }}
            className={clsx(
              'ml-0.5 inline-flex size-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <X className="size-3.5" />
          </button>
        );

        return (
          <span
            key={a.localId}
            className={clsx(chipBaseClasses, 'pl-3', 'pr-1.5', isUploading && 'bg-muted/80')}
          >
            <Link2 className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="whitespace-nowrap">{truncated}</span>
            {isUploading && <Spinner className="size-3.5 text-muted-foreground" />}
            {closeButton}
          </span>
        );
      })}
    </div>
  );
}
